import { useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { XIcon, CheckIcon } from '@phosphor-icons/react'

import { Button } from '@/components/ui/button'
import { Icon as NewIcon } from '@/components/ui/icon'
import { useApproveRejectComment } from '@/api/groups/approve-reject-comment'

import Avatar from '../../../../../Avatar'
import Icon from '../../../../Icons'
import ImagesContainer from '../../../../ImagesContainer/ImagesContainer'
import PicturesModal from '../../../../Modal/PicturesModal'
import { getAssetLink } from '../../../../../helpers/get-asset-link'

const PostComment = ({
  comment: { id, author, message, createdAt, likes, images, approved },
  currentUser,
  onDeleteComment,
  onLikeDislike,
  readOnlyMode = false,
  ref,
  postId,
  groupId,
  approvedCommentsOn,
  onApproveRejectCallback,
  isSubmitting,
}) => {
  const [isPicturesModalVisible, setPicturesModalVisible] = useState(false)
  const { mutate: approveRejectComment, isPending } = useApproveRejectComment()

  const isLiked = !!(likes && likes.find((like) => like.author.email === currentUser.email))
  const shouldShowApproveButtons = approvedCommentsOn && !approved
  const shouldShowRemoveButton = !readOnlyMode && approved

  const buttonDisabled = isSubmitting || isPending

  const onAproveRejectButtonClick = async (approve) => {
    if (!postId || !groupId || !id || isPending || isSubmitting) {
      return
    }

    await approveRejectComment({
      approve,
      commentId: id,
      groupId,
      postId,
    })

    onApproveRejectCallback?.()
  }

  return (
    <div key={id} ref={ref} className="groups-post-comment">
      <div className="groups-post-comment__avatar-container">
        <Avatar size="big">
          <img src={getAssetLink({ path: 'chitown-avatar/avatar', file: author.avatar })} alt="" />
        </Avatar>
      </div>
      <div className="groups-post-comment__container">
        <div className="comment-header">
          <div className="comment-header__txt">
            <span className="author-header">
              <span className="author-name">{`${author.firstName} ${author.lastName}`}</span>
              {shouldShowRemoveButton && (
                <button
                  className="remove-button"
                  onClick={onDeleteComment}
                  disabled={buttonDisabled}
                >
                  Remove
                </button>
              )}
            </span>
            <span className="author-message-comment">{message}</span>
            {images.length > 0 && (
              <div className="post-small-card__images-preview">
                <ImagesContainer images={images} onClick={() => setPicturesModalVisible(true)} />
              </div>
            )}
          </div>
        </div>
        <div className="comment-footer">
          <div className="author-message-comment-time">{moment(createdAt).fromNow()}</div>
          {approved && (
            <div className="post-small-card__toolbar-wrap-buttons">
              <div className="post-small-card__toolbar-button">
                <Icon icon="heart" viewBox={'0 -2 14 18'} fill={'#E03838'} />
                <span>{likes ? `${likes.length}` : '0'}</span>
              </div>
              {!readOnlyMode && (
                <div
                  className="post-small-card__toolbar-button clickable"
                  onClick={() => {
                    if (readOnlyMode) {
                      return
                    }
                    onLikeDislike(id, isLiked)
                  }}
                >
                  <Icon
                    icon="heart"
                    viewBox={'0 -2 14 18'}
                    fill={isLiked ? 'grey' : 'transparent'}
                    svgProps={{ stroke: 'grey' }}
                  />
                  <span>{isLiked ? 'Dislike it' : 'Like it'}</span>
                </div>
              )}
            </div>
          )}
        </div>
        {shouldShowApproveButtons && (
          <div className="comment-footer-button">
            <Button
              use="secondary"
              size="small"
              onClick={() => onAproveRejectButtonClick(false)}
              disabled={buttonDisabled}
            >
              <NewIcon icon={XIcon} color="red500" />
              Decline
            </Button>
            <Button
              use="primary"
              size="small"
              onClick={() => onAproveRejectButtonClick(true)}
              disabled={buttonDisabled}
            >
              Approve
              <NewIcon icon={CheckIcon} />
            </Button>
          </div>
        )}
      </div>
      <PicturesModal
        assetPath="chitown-images/groups"
        visible={isPicturesModalVisible}
        pictures={images}
        onCloseModal={() => setPicturesModalVisible(false)}
      />
    </div>
  )
}

PostComment.propTypes = {
  comment: PropTypes.shape({
    createdAt: PropTypes.number,
    author: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      avatar: PropTypes.string,
    }),
    id: PropTypes.number,
  }),
  currentUser: PropTypes.object,
  onDeleteComment: PropTypes.func,
  onLikeDislike: PropTypes.func,
  readOnlyMode: PropTypes.bool,
  approvedCommentsOn: PropTypes.bool,
  postId: PropTypes.number,
  groupId: PropTypes.number,
}

export default PostComment
